import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import ArgonButton from "components/ArgonButton";
import SubjectTableData from "Views/Curriculum/data/subjectTableData";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { baseURL } from "utils/api";
import axios from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";

function index() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Add/Edit dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [subjectName, setSubjectName] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      // Fetch all classes without pagination
      const response = await axios.get(`${baseURL}/classes?limit=100`);
      console.log("Class API Response:", response.data);
      if (response.data.success) {
        // Filter out deleted classes
        const allClasses = (response.data.data || []).filter((cls) => !cls.isDeleted);
        console.log("All Classes:", allClasses);
        setClasses(allClasses);
        if (allClasses.length === 0) {
          toast.info("No classes available. Please create a class first.");
        }
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to load classes"
      );
    }
  };

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  const handleAddClick = () => {
    setFormMode("add");
    setEditId(null);
    setSubjectName("");
    setSelectedClasses([]);
    setFormOpen(true);
  };

  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setSubjectName(name || "");
    // Fetch subject details to get classes
    axios
      .get(`${baseURL}/subjects/${id}`)
      .then((res) => {
        if (res.data.success) {
          // Handle classes array - map to IDs if objects are returned
          const classIds =
            res.data.data.classes?.map((cls) => (typeof cls === "object" ? cls._id : cls)) || [];
          setSelectedClasses(classIds);
        }
      })
      .catch((err) => {
        console.error("Error fetching subject details:", err);
      });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setSubjectName("");
    setSelectedClasses([]);
    setEditId(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!subjectName.trim()) {
      toast.error("Subject name cannot be empty");
      return;
    }
    if (!selectedClasses || selectedClasses.length === 0) {
      toast.error("Please select at least one class");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/subjects`, { name: subjectName, classes: selectedClasses })
        .then((res) => {
          toast.success(res.data?.message || "Subject added successfully");
          setRefresh((p) => !p);
          handleCloseForm();
        })
        .catch((err) =>
          toast.error(
            err.response?.data?.error || err.response?.data?.message || "Failed to add subject"
          )
        )
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/subjects/${editId}`, { name: subjectName, classes: selectedClasses })
        .then((res) => {
          toast.success(res.data?.message || "Subject updated successfully");
          setRefresh((p) => !p);
          handleCloseForm();
        })
        .catch((err) =>
          toast.error(
            err.response?.data?.error || err.response?.data?.message || "Failed to update subject"
          )
        )
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    // Soft delete: set isDeleted to true
    axios
      .put(`${baseURL}/subjects/${deleteId}`, { isDeleted: true })
      .then((res) => {
        toast.success(res.data?.message || "Subject deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to delete subject");
      });
  };

  const { columns, rows } = SubjectTableData(handleDeleteClick, handleEditClick, refresh);

  return (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={4} mb={3}>
        <ArgonTypography variant="h6" size="small">
          All Subject
        </ArgonTypography>
        <ArgonButton variant="contained" color="primary" size="small" onClick={handleAddClick}>
          Add Subject
        </ArgonButton>
      </ArgonBox>
      <ArgonBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              No subject found
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Click &apos;Add Subject&apos; to create a new subject.
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>

      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Subject"
        description="Are you sure you want to delete this subject?"
        confirmText="Delete Subject"
        cancelText="Cancel"
      />

      {/* Add/Edit Subject Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5">
              {formMode === "add" ? "Add Subject" : "Edit Subject"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            {/* Multiple Class Selection with Autocomplete */}
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" mb={1}>
                Select Classes *
              </ArgonTypography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                id="class-autocomplete"
                options={classes}
                value={classes.filter((cls) => selectedClasses.includes(cls._id))}
                onChange={(event, newValue) => {
                  setSelectedClasses(newValue.map((cls) => cls._id));
                }}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                disabled={formLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select classes"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "45px",
                        border: "1px solid #d2d6da",
                        borderRadius: "8px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      label={option.name}
                      deleteIcon={<CancelIcon />}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        "& .MuiChip-deleteIcon": {
                          color: "white",
                          "&:hover": {
                            color: "#f0f0f0",
                          },
                        },
                      }}
                    />
                  ))
                }
              />
            </ArgonBox>

            {/* Subject Name */}
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" mb={1}>
                Subject Name
              </ArgonTypography>
              <ArgonBox
                component="input"
                type="text"
                value={subjectName}
                onChange={(e) => setSubjectName(e.target.value)}
                placeholder="Enter Subject Name"
                disabled={formLoading}
                sx={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d2d6da",
                  borderRadius: "8px",
                }}
              />
            </ArgonBox>
          </DialogContent>
          <DialogActions>
            <ArgonButton
              onClick={handleCloseForm}
              variant="outlined"
              color="secondary"
              disabled={formLoading}
            >
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" variant="contained" color="primary" disabled={formLoading}>
              {formMode === "add"
                ? formLoading
                  ? "Adding..."
                  : "Add Subject"
                : formLoading
                ? "Updating..."
                : "Update Subject"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
    </ArgonBox>
  );
}

export default index;
